import React, { useState, useEffect } from "react";
import AceEditor from "react-ace";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/useAuth";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import MenuBar from "@/components/MenuBar";
import { Save, Play, History } from "lucide-react";
import {
  Script,
  GetScriptsResponse,
  ExecuteScriptResponse,
} from "@/types/script";

import "ace-builds";
import "ace-builds/webpack-resolver";
import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/ext-language_tools";
import "ace-builds/src-noconflict/keybinding-vim";

export default function AdminEditor() {
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [output, setOutput] = useState("");
  const [isExecuting, setIsExecuting] = useState(false);
  const [scriptName, setScriptName] = useState("");
  const [scripts, setScripts] = useState<Script[]>([]);
  const [selectedScript, setSelectedScript] = useState<string>("");
  const [editorRef, setEditorRef] = useState<any>(null);

  useEffect(() => {
    fetchScripts();
  }, []);

  useEffect(() => {
    // Simple editor refresh after mount
    if (editorRef && editorRef.editor) {
      const refreshEditor = () => {
        if (editorRef.editor && editorRef.editor.renderer) {
          editorRef.editor.renderer.updateCharacterSize();
          editorRef.editor.renderer.updateFull();
          editorRef.editor.resize();
        }
      };

      setTimeout(refreshEditor, 100);

      // Add window resize listener
      window.addEventListener("resize", refreshEditor);

      return () => {
        window.removeEventListener("resize", refreshEditor);
      };
    }
  }, [editorRef]);

  const fetchScripts = async () => {
    try {
      const response = await ApiService.request<GetScriptsResponse>(
        EndpointKeys.GET_ADMIN_SCRIPTS,
      );
      setScripts(response.scripts);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch scripts",
        variant: "destructive",
      });
    }
  };

  const handleScriptSelect = (scriptId: string) => {
    const script = scripts.find((s) => s.id.toString() === scriptId);
    if (script) {
      setCode(script.code);
      setScriptName(script.name);
      setSelectedScript(scriptId);
    } else {
      setCode("");
      setScriptName("");
      setSelectedScript("");
    }
  };

  const handleSaveScript = async () => {
    if (!scriptName) {
      toast({
        title: "Error",
        description: "Please enter a script name",
        variant: "destructive",
      });
      return;
    }

    if (!code.trim()) {
      toast({
        title: "Error",
        description: "Cannot save empty script",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await ApiService.request<ExecuteScriptResponse>(
        EndpointKeys.SAVE_ADMIN_SCRIPT,
        {
          method: "POST",
          body: {
            code,
            name: scriptName,
            script_id: selectedScript ? parseInt(selectedScript) : undefined,
          },
        },
      );

      const savedScript = (response as any).script as Script;

      if (savedScript && savedScript.id) {
        setSelectedScript(savedScript.id.toString());
        setScriptName(savedScript.name);
        await fetchScripts();
        toast({ title: "Success", description: "Script saved successfully" });
      } else {
        toast({
          title: "Warning",
          description: "Script save confirmation not received.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to save script: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    }
  };

  const executeCode = async (codeToExecute?: string) => {
    const currentCode = codeToExecute || code;
    if (!currentCode.trim()) {
      toast({ title: "Info", description: "Cannot execute empty code." });
      return;
    }

    try {
      setIsExecuting(true);
      setOutput("Executing & Saving...");

      const response = await ApiService.request<ExecuteScriptResponse>(
        EndpointKeys.EXECUTE_ADMIN_CODE,
        {
          method: "POST",
          body: {
            code: currentCode,
            script_id: selectedScript ? parseInt(selectedScript) : undefined,
            name: scriptName,
          },
        },
      );

      setOutput(
        response.output ||
          (response.success
            ? "Execution successful, no output."
            : "Execution failed."),
      );

      if (response.success) {
        toast({
          title: "Success",
          description: "Code executed successfully.",
        });
      } else {
        toast({
          title: "Execution Finished",
          description:
            response.error ||
            "Execution failed with no specific error message.",
          variant: "destructive",
        });
      }

      if (response.script_id) {
        const returnedScriptIdStr = response.script_id.toString();
        setSelectedScript(returnedScriptIdStr);

        if (response.script_name) {
          setScriptName(response.script_name);
        }

        await fetchScripts();
      } else {
        if (response.success) {
          console.warn(
            "Execution successful, but no script ID returned from backend save/update.",
          );
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      setOutput(`Execution Request Failed: ${errorMessage}`);
      toast({
        title: "API Error",
        description: `Failed to send execution request: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <div
        className={`${"hidden"} md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>
      <div className="flex-1 md:ml-0 overflow-hidden mt-16 md:mt-0">
        <div className="p-4 md:p-6">
          <Card className="mt-6 relative z-10">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <h2 className="text-2xl">Admin Code Editor</h2>
                <div className="flex gap-4 flex-wrap">
                  <div className="flex-grow min-w-[200px]">
                    <Select
                      value={selectedScript}
                      onValueChange={handleScriptSelect}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Load existing or save new..." />
                      </SelectTrigger>
                      <SelectContent>
                        {scripts.map((script) => (
                          <SelectItem
                            key={script.id}
                            value={script.id.toString()}
                          >
                            {script.name} ({script.execution_count} runs)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-grow min-w-[200px]">
                    <Input
                      placeholder="Script name (required for saving)"
                      value={scriptName}
                      onChange={(e) => setScriptName(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="h-[500px] border rounded-lg overflow-hidden relative z-0">
                  <style>{`
                #admin-editor .ace_editor,
                #admin-editor .ace_content,
                #admin-editor .ace_text-layer,
                #admin-editor .ace_line,
                #admin-editor .ace_cursor-layer,
                #admin-editor * {
                  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace !important;
                  font-size: 14px !important;
                }
              `}</style>
                  <AceEditor
                    mode="python"
                    theme="monokai"
                    onChange={setCode}
                    value={code}
                    name="admin-editor"
                    editorProps={{ $blockScrolling: true }}
                    width="100%"
                    height="100%"
                    keyboardHandler="vim"
                    onLoad={(editor) => {
                      // Store the editor instance
                      setEditorRef({ editor });

                      // Let editor use its default font and just refresh layout
                      if (editor && editor.renderer) {
                        editor.renderer.updateCharacterSize();
                        editor.renderer.updateFull();
                        editor.resize();
                      }

                      editor.commands.addCommand({
                        name: "executeCode",
                        bindKey: { win: "Shift-Enter", mac: "Shift-Enter" },
                        exec: () => {
                          const currentCode = editor.getValue();
                          if (currentCode.trim()) {
                            executeCode(currentCode);
                          }
                        },
                      });
                    }}
                    setOptions={{
                      showLineNumbers: true,
                      tabSize: 4,
                      useSoftTabs: true,
                      wrap: false,
                      enableBasicAutocompletion: true,
                      enableLiveAutocompletion: true,
                      enableSnippets: true,
                    }}
                  />
                </div>

                <div className="flex gap-4 flex-wrap">
                  <Button
                    onClick={() => executeCode()}
                    disabled={isExecuting || !code.trim()}
                    className="flex-grow"
                    title="Execute code and save changes (Shift+Enter)"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {isExecuting ? "Executing & Saving..." : "Execute & Save"}
                  </Button>
                  <Button
                    onClick={handleSaveScript}
                    disabled={isExecuting || !code.trim() || !scriptName.trim()}
                    variant="outline"
                    className="flex-grow"
                    title={
                      selectedScript
                        ? "Save changes to the selected script"
                        : "Save the current code as a new script"
                    }
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {selectedScript ? "Save Changes" : "Save as New Script"}
                  </Button>
                </div>

                {output && (
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold mb-2">Output:</h3>
                    <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto text-sm">
                      {output}
                    </pre>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
